import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';

import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { CompanyFilterDTO, UserDTO, OportunityDTO, OportunityFilterDTO, ValueListDTO, AccessRoleDTO, AccessRoleFilterDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField, TextFieldInput, KanbanList, ISwitchInputProps } from '../../../shared/components';
import { OportunityMaintenance } from './OportunityMaintenance';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { ListId, LocalStorange } from '../../../shared/environment';
import { CompanyDTO } from '../../../shared/interfaces/corpSphere/CompanyDTO';

export const Oportunity: React.FC = () => {

    const [companyList, setCompanyList] = useState<CompanyDTO[]>([]);
    const [statusList, setStatusList] = useState<ValueListDTO[]>([]);
    const [sourceList, setSourceList] = useState<ValueListDTO[]>([]);
    const [priorityList, setPriorityList] = useState<ValueListDTO[]>([]);
    const [ownerList, setOwnerList] = useState<UserDTO[]>([]);
    const [oportunityList, setOportunityList] = useState<OportunityDTO[]>([]);
    const [oportunityFilterDTO, setOportunityFilterDTO] = useState<OportunityFilterDTO>();

    const [cardMoved, setCardMoved] = useState<any>();
    const [checkedKanban, setCheckedKanban] = useState(true);

    const [actionExecuted, setActionExecuted] = useState(false);

    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [expadedData, setExpandedData] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);
    const [proposal, setProposal] = useState('');

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    useEffect(() => {
        setOportunityFilterDTO({
            ...oportunityFilterDTO,
            jobTitleId: '',
            code: '',
            seniorityId: '',
            statusId: '',
            requesterId: '',
            managerId: '',
            companyId: ''
        } as OportunityFilterDTO);
    }, []);


    useEffect(() => {
        handleListGetAll(ListId.clientsphere.oportunity.statusId, setStatusList);
        handleListGetAll(ListId.clientsphere.oportunity.sourceId, setSourceList);
        handleListGetAll(ListId.clientsphere.oportunity.priorityId, setPriorityList);

        handleCompanyGetAll();
        handleOwnerGetAllAsync();
        handleOportunityGetAllViewAsync();

        handleGetPermission(ListId.clientsphere.oportunityId, setAccessRolePermissionDTO);
    }, [oportunityFilterDTO]);

    useEffect(() => {
        if (actionExecuted)
            handleOportunityGetAllViewAsync();
    }, [actionExecuted]);

    useEffect(() => {
        handleMoveTaskKanbanAsync();
    }, [cardMoved]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync', accessRoleFilterDTO, setActionExecuted, setLoading, undefined, setList);
    }, []);

    const handleMoveTaskKanbanAsync = useCallback(async () => {
        if (cardMoved === undefined || cardMoved.id === undefined || cardMoved.statusId === undefined) {
            return;
        }
        const oportunityId = cardMoved.id;
        const statusId = cardMoved.statusId;
        const index = cardMoved.index;

        const oportunityDTO = {
            id: oportunityId,
            statusId: statusId,
            kanbanIndex: index,
            updatedById: userInfo.user.Id
        };
        GenericService.PostGenericAsync1('clientsphere.oportunity', 'MoveTaskKanbanAsync', oportunityDTO, setActionExecuted, setLoading, showMessage);
    }, [cardMoved]);

    const handleCompanyGetAll = useCallback(async () => {
        const companyFIlterDTO: CompanyFilterDTO = {
            name: '',
            businessRelationshipTypeId: '',
            statusId: '',
            orderBy: 'name ASC'
        };
        GenericService.GetAllAsync1('corpsphere.company', companyFIlterDTO, setLoading, setCompanyList, showMessage);
    }, []);

    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'valueindex, value ASC'
        };
        GenericService.GetAllAsync1(
            'parmsphere.valueList',
            valueListFilterDTO,
            setLoading,
            setList,
            showMessage
        );
    }, []);

    const handleOwnerGetAllAsync = useCallback(async () => {
        const userFilterDTO = {
            companyId: oportunityFilterDTO?.companyId,
        };
        GenericService.GetAllAsync1('authsphere.user', userFilterDTO, setLoading, setOwnerList, showMessage);
    }, [oportunityFilterDTO]);

    const handleOportunityGetAllViewAsync = useCallback(async () => {
        if (oportunityFilterDTO === undefined)
            return;
        if (accessRolePermissionDTO?.allowGetAllView == undefined)
            return;
        if (!accessRolePermissionDTO?.allowGetAllView) {
            showMessage('There is no permission for get all view.', 'info');
            return;
        } if (accessRolePermissionDTO?.allowGetAllView == undefined)
            return;
        if (!accessRolePermissionDTO?.allowGetAllView) {
            showMessage('There is no permission for get all view.', 'info');
            return;
        }
        GenericService.GetAllViewAsync1('clientsphere.oportunity', oportunityFilterDTO, setActionExecuted, setLoading, setOportunityList, showMessage, true);
    }, [oportunityFilterDTO, accessRolePermissionDTO]);

    const handleClearFilterClick = () => {
        setOportunityFilterDTO({
            ...oportunityFilterDTO,
            name: '',
            statusId: '',
            sourceId: '',
            companyId: userInfo.user.companyId
        } as OportunityFilterDTO);
    };

    function handleSearchClick() {
        handleOportunityGetAllViewAsync();
    }

    const handleNew = () => {
        if (!accessRolePermissionDTO?.allowAdd) {
            showMessage('There is no permission for new record.', 'info');
            return;
        }
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setIsOpenMaintenance(true);
        setProposal('');
    };

    const handleViewDetatils = (data: string) => {
        if (!accessRolePermissionDTO?.allowGetById) {
            showMessage('There is no permission for view details.', 'info');
            return;
        }
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setIsOpenMaintenance(true);
        setProposal('');
    };

    const handleDelete = (data: string) => {
        if (!accessRolePermissionDTO?.allowDelete) {
            showMessage('There is no permission for delete record.', 'info');
            return;
        }
        setNewRecord('');
        setEditRecord('');
        setDeleteRecord(data);
        setIsOpenMaintenance(true);
        setProposal('');
    };

    const handleProposal = (data: string) => {
        setNewRecord('');
        setEditRecord('');
        setDeleteRecord('');
        setIsOpenMaintenance(true);
        setProposal(data);
    };

    const toolBarActions: IDialogActions[] = [
        { id: 'new', text: 'New Oportunity', icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: 'View Details', icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'delete', toolTip: 'Delete', icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
        { id: 'proposal', toolTip: 'Proposal', icon: 'gavel_outlined', iconColor: 'info', setAction: handleProposal },
    ];

    const chipFilters: IChipFilter[] = [
        { label: 'Total Records', value: oportunityList.length },
        { label: 'Name', value: oportunityFilterDTO?.name },
        { label: 'Status', value: statusList.find(x => x.id === oportunityFilterDTO?.statusId)?.value },
        { label: 'Source', value: sourceList.find(x => x.id === oportunityFilterDTO?.sourceId)?.value },
        { label: 'Company', value: companyList.find(x => x.id === oportunityFilterDTO?.companyId)?.id },
    ];

    const columns: ICardColumns[] = [
        { field: 'name', label: 'Name', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'code', label: 'code', size: 2, type: 'text', visible: true, isChip: true, hasFieldLine: false, hasToolTip: true },
        { field: 'startDateTime', label: 'Start Date', size: 2, type: 'date', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'expectedClosingDateTime', label: 'Expected Closing', size: 2, type: 'date', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'estimatedValue', label: 'Estimate Value', size: 1, type: 'currency', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'priority', label: 'Priority', size: 1, type: 'text', visible: true, isChip: true, hasFieldLine: false, hasToolTip: true },
        { field: 'qualification', label: 'Qualification', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: false },
        { field: 'owner', label: 'Owner', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: false },
        { field: 'owner', label: 'Owner', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: false },
        { field: 'status', label: 'Status', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: false },
        { field: 'company', label: 'Company', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },

    ];

    const kanbanColumns: ICardColumns[] = [
        { field: 'name', label: 'Name', size: 12, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'code', label: 'code', size: 12, type: 'text', visible: true, isChip: true, hasFieldLine: false, hasToolTip: true },
        { field: 'startDateTime', label: 'Start Date', size: 12, type: 'date', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'expectedClosingDateTime', label: 'Expected Closing', size: 12, type: 'date', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'owner', label: 'Owner', size: 12, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: false },
        { field: 'company', label: 'Company', size: 12, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },

    ];

    const kanbanSwitch: ISwitchInputProps = {
        id: 'oportunity.switch', label: 'View Kanban|View List', checked: checkedKanban, setChecked: setCheckedKanban, disabled: false, autoFocus: false, color: 'info', size: 'small', labelPlacement: 'end'
    };

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }

    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title='OPORTUNITY'>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                    kanbanSwitch={kanbanSwitch}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={3}>
                            <TextFieldInput
                                id='name'
                                label='Name'
                                setValue={setOportunityFilterDTO}
                                value={oportunityFilterDTO?.name}
                                obj={oportunityFilterDTO}
                                placeholder={'Code'}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='companyId'
                                label='Company'
                                setValue={setOportunityFilterDTO}
                                value={oportunityFilterDTO?.companyId}
                                obj={oportunityFilterDTO}
                                valueList={companyList}
                                hasNoneSelect={true}
                            />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='sourceId'
                                label='source'
                                setValue={setOportunityFilterDTO}
                                value={oportunityFilterDTO?.sourceId}
                                obj={oportunityFilterDTO}
                                valueList={sourceList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='priorityId'
                                label='Priority'
                                setValue={setOportunityFilterDTO}
                                value={oportunityFilterDTO?.priorityId}
                                obj={oportunityFilterDTO}
                                valueList={priorityList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='statusId'
                                label='Status'
                                setValue={setOportunityFilterDTO}
                                value={oportunityFilterDTO?.statusId}
                                obj={oportunityFilterDTO}
                                valueList={statusList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='ownerId'
                                label='Owner'
                                setValue={setOportunityFilterDTO}
                                value={oportunityFilterDTO?.ownerId}
                                obj={oportunityFilterDTO}
                                valueList={ownerList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                {checkedKanban ? (
                    <Box sx={{ display: 'flex', height: 'calc(100vh - 280px)', minHeight: 520, width: '100%', overflow: 'hidden' }}>
                        <KanbanList
                            identifierField='statusId'
                            cards={oportunityList}
                            attributes={kanbanColumns}
                            statusList={statusList}
                            setRowExpanded={setExpandedData}
                            setCardMoved={setCardMoved}
                            actions={cardActions}
                            cardSize={2}
                        />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', maxHeight: '80vh', width: '100%', overflow: 'hidden', overflowY: 'auto' }}>
                        <CardList
                            columns={columns}
                            rows={oportunityList}
                            actions={cardActions}
                            cardSize={12}
                            hasChildren={false}
                            setRowExpanded={() => void undefined}
                        />
                    </Box>
                )}
                {newRecord && (
                    <OportunityMaintenance
                        data={''}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {editRecord && (
                    <OportunityMaintenance
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {deleteRecord && (<Delete
                    entity={'clientsphere.oportunity'}
                    data={deleteRecord}
                    setIsOpenDiaglog={setIsOpenMaintenance}
                    isOpenDialog={isOpenMaintenance}
                    setActionExecuted={setActionExecuted}
                />)}
            </LayoutDashboard>
        </React.Fragment >
    );
};
